import { useEffect, useMemo, useState } from 'react';
import { _isNil, _notNil } from '@/littledash';
import type { UserApiId } from '@/model/User.model';
import type { AvatarColor } from '@/constants/colors';
import { useDataTable } from '@/components/UI/DataTableV2/DataTableProvider';
import {
  type DataTableRemoteSelectionChangeEvent,
  DataTableEvent,
  type DataTableCellIdCoordinate,
  type DataTableCellReference,
} from '@/components/UI/DataTableV2/DataTable.model';
import { toCellRef } from '@/components/UI/DataTableV2/DataTable.util';
import { useOnlineUsers } from './useOnlineUsers';
import type { components } from '@/generated/messaging-api/openapi-types';

type DataTableSelectionV1 = components['schemas']['DataTableSelectionV1.schema'];

export interface RemoteSelection {
  userId: UserApiId;
  userName: string;
  color: AvatarColor;
  top: number;
  left: number;
  width: number;
  height: number;
  fromCoordinate: DataTableCellIdCoordinate;
}

export interface RemoteSelectionCell {
  cellRef: DataTableCellReference;
  top: number;
  left: number;
  width: number;
  height: number;
  fromCoordinate: DataTableCellIdCoordinate;
  users: ReadonlyArray<RemoteSelection>;
  color: AvatarColor;
}

type MutableRemoteSelectionCell = Omit<RemoteSelectionCell, 'users'> & { users: Array<RemoteSelection> };

export interface RemoteSelectionsModel {
  byUser: ReadonlyMap<UserApiId, RemoteSelection>;
  userToCellRef: ReadonlyMap<UserApiId, DataTableCellReference>;
  cellsByRef: ReadonlyMap<DataTableCellReference, RemoteSelectionCell>;
  /** Keyed by `gridPositionKey(scrollableColumnIndex, rowIndex)` for O(1) pointer hit-testing. */
  cellRefByPosition: ReadonlyMap<string, DataTableCellReference>;
  cells: ReadonlyArray<RemoteSelectionCell>;
}

/**
 * Position key for a cell within the scrollable grid, used to resolve which
 * remote selection (if any) the pointer is over in O(1).
 */
export const gridPositionKey = (scrollableColumnIndex: number, rowIndex: number): string =>
  `${scrollableColumnIndex}:${rowIndex}`;

const EMPTY_MODEL: RemoteSelectionsModel = {
  byUser: new Map(),
  userToCellRef: new Map(),
  cellsByRef: new Map(),
  cellRefByPosition: new Map(),
  cells: [],
};

/**
 * Subscribes to RemoteSelectionChange events and builds a memoised model of
 * every active remote user's "from" cell selection. Grouping is a single O(n)
 * pass; all subsequent reads (by user, by cell, follow/pulse checks) are O(1)
 * map lookups.
 *
 * Only the "from" cell (origin) of each selection is rendered. Only selections
 * for active users (last_active within 60 seconds) are included. Selections with
 * deleted columns/rows or in pinned columns are skipped.
 *
 * Users that share a cell are grouped into a single `RemoteSelectionCell` whose
 * `users` are ordered most-recently-active first (matching the server order
 * from `useOnlineUsers`) and whose `color` is the most-recent user's color.
 */
export const useRemoteSelections = (): RemoteSelectionsModel => {
  const { dataTableService, users } = useDataTable();
  const { byId: onlineById, activeUserIds } = useOnlineUsers();
  const [selectionByUserId, setSelectionByUserId] = useState<ReadonlyMap<UserApiId, DataTableSelectionV1>>(new Map());

  useEffect(() => {
    const abortController = new AbortController();
    const handler = (event: DataTableRemoteSelectionChangeEvent): void => {
      // Each selection_change event carries only the user(s) whose selection
      // changed, not the full set, so we accumulate by user id rather than
      // replace. A user moving cells overwrites their own entry.
      setSelectionByUserId((current) => {
        const next = new Map(current);
        event.detail.selections.forEach((sel) => {
          next.set(sel.user_api_id, sel);
        });
        return next;
      });
    };
    dataTableService.subscribe(DataTableEvent.RemoteSelectionChange, handler, abortController.signal);
    return () => {
      abortController.abort('teardown');
    };
  }, [dataTableService]);

  useEffect(() => {
    // Bound the selection map by concurrent participants: drop selections for
    // users who are no longer online so it can't grow unbounded across a
    // long-lived session with user churn.
    setSelectionByUserId((current) => {
      let changed = false;
      const next = new Map(current);
      next.forEach((_sel, userId) => {
        if (!onlineById.has(userId)) {
          next.delete(userId);
          changed = true;
        }
      });
      return changed ? next : current;
    });
  }, [onlineById]);

  const pinnedColumnCount = useMemo(() => {
    const pinnedColumnIds = new Set(dataTableService.getPinnedColumns().map((p) => p.id));
    return dataTableService.leafColumns.reduce((count, col) => (pinnedColumnIds.has(col.id) ? count + 1 : count), 0);
  }, [dataTableService]);

  const { cellWidth, cellHeight } = dataTableService.dimensions;

  return useMemo(() => {
    const byUser = new Map<UserApiId, RemoteSelection>();
    const userToCellRef = new Map<UserApiId, DataTableCellReference>();
    const cellsByRef = new Map<DataTableCellReference, MutableRemoteSelectionCell>();
    const cellRefByPosition = new Map<string, DataTableCellReference>();
    const cells: Array<MutableRemoteSelectionCell> = [];

    onlineById.forEach((onlineUser) => {
      if (!activeUserIds.has(onlineUser.apiId)) {
        return;
      }

      const sel = selectionByUserId.get(onlineUser.apiId);
      if (_isNil(sel)) {
        return;
      }

      const studyUser = users.get(onlineUser.apiId);
      if (_isNil(studyUser)) {
        return;
      }

      const fromColIndex = dataTableService.toIndexColumn(sel.from.column);
      const fromRowIndex = dataTableService.toIndexRow(sel.from.row);
      if (_isNil(fromColIndex) || _isNil(fromRowIndex)) {
        return;
      }
      if (fromColIndex < pinnedColumnCount) {
        return;
      }

      const adjustedCol = fromColIndex - pinnedColumnCount;
      const left = adjustedCol * cellWidth;
      const top = fromRowIndex * cellHeight;

      const remoteSelection: RemoteSelection = {
        userId: onlineUser.apiId,
        userName: studyUser.name,
        color: onlineUser.color,
        top,
        left,
        width: cellWidth,
        height: cellHeight,
        fromCoordinate: sel.from,
      };

      const cellRef = toCellRef(sel.from);
      byUser.set(onlineUser.apiId, remoteSelection);
      userToCellRef.set(onlineUser.apiId, cellRef);

      const existingCell = cellsByRef.get(cellRef);
      if (_isNil(existingCell)) {
        const cell: MutableRemoteSelectionCell = {
          cellRef,
          top,
          left,
          width: cellWidth,
          height: cellHeight,
          fromCoordinate: sel.from,
          users: [remoteSelection],
          color: remoteSelection.color,
        };
        cellsByRef.set(cellRef, cell);
        cellRefByPosition.set(gridPositionKey(adjustedCol, fromRowIndex), cellRef);
        cells.push(cell);
      } else {
        existingCell.users.push(remoteSelection);
      }
    });

    if (cells.length === 0) {
      return EMPTY_MODEL;
    }

    return { byUser, userToCellRef, cellsByRef, cellRefByPosition, cells };
  }, [selectionByUserId, onlineById, activeUserIds, users, dataTableService, pinnedColumnCount, cellWidth, cellHeight]);
};
